// In-game co-driver: drives the Caller from the car's progress along the stage, speaks through
// CodriverVoice and feeds the pace-note HUD. Reads the opening notes before the start, re-syncs
// after a reset, and stays silent while paused.
import { Caller, type Call, type CallTiming } from './caller';
import type { StageNotes } from './notes';
import type { CodriverVoice } from './voice';
import type { PaceNoteHud } from '../ui/pacenotes';

export interface CodriverSettings {
  timing: CallTiming;
  voice: boolean;
  symbols: boolean;
  subtitles: boolean;
}

export const DEFAULT_CODRIVER: CodriverSettings = { timing: 'normal', voice: true, symbols: true, subtitles: true };

export class CodriverRuntime {
  readonly caller: Caller;
  private readonly voice: CodriverVoice;
  private readonly hud: PaceNoteHud | null;
  private readonly startS: number;
  /** The opening notes have been read (or skipped because the car was already moving). */
  preStartDone = false;
  private paused = false;
  lastCall: Call | null = null;

  constructor(notes: StageNotes, startS: number, voice: CodriverVoice, hud: PaceNoteHud | null, settings: CodriverSettings) {
    this.voice = voice;
    this.hud = hud;
    this.startS = startS;
    this.caller = new Caller(notes, { timing: settings.timing, durationOf: voice.durationOf });
    this.caller.onCall = (c) => this.deliver(c);
    this.apply(settings);
  }

  /** Cockpit view with the co-driver figure: the notes move onto his tablet (the HUD strip hides). */
  setCockpit(on: boolean): void {
    if (this.hud) this.hud.stripHidden = on;
  }

  apply(s: CodriverSettings): void {
    this.caller.opts.timing = s.timing;
    this.voice.enabled = s.voice;
    if (this.hud) {
      this.hud.showSymbols = s.symbols;
      this.hud.showSubtitles = s.subtitles;
    }
  }

  private now = 0;

  private deliver(c: Call): void {
    this.lastCall = c;
    this.voice.speak(c);
    this.hud?.onCall(c, this.now);
  }

  /** True once the voice can actually be heard (or the fallback is active). */
  private voiceReady(): boolean {
    return this.voice.mode !== 'none' || !this.voice.enabled;
  }

  /**
   * Per frame with the car's distance along the stage (m), speed (m/s) and the sim clock (s).
   * Before the start line the opening notes are read once the voice is ready.
   */
  update(s: number, speed: number, now: number): void {
    this.now = now;
    if (this.paused) return;
    if (!this.preStartDone) {
      if (s < this.startS - 2 && speed < 1) {
        if (!this.voiceReady()) return;
        this.preStartDone = true;
        this.caller.resync(Math.min(s, this.startS - 1), now);
        this.caller.preStart(this.startS, now);
      } else {
        this.preStartDone = true;
        this.caller.resync(s, now);
      }
    }
    this.caller.update(s, speed, now);
    this.hud?.update(s, now);
  }

  /** Sim time when the co-driver stops talking. */
  get busyUntil(): number {
    return this.caller.busyUntil;
  }

  /** Can the co-driver be heard (sprite decoded or the Web Speech fallback active)? */
  get audible(): boolean {
    return this.voice.enabled && this.voice.mode !== 'none';
  }

  /** A one-off call outside the notes (start countdown, "good" after the finish). */
  say(tokens: string[], text: string): void {
    if (this.paused) return;
    this.deliver({ notes: [], tokens, text, t: this.now, duration: this.voice.durationOf(tokens), s: 0, compressed: false });
  }

  /** After a reset or an off: drop the queue and pick up from the car's new position. */
  resync(s: number, now: number): void {
    this.voice.stop();
    this.hud?.clear();
    this.caller.resync(s, now);
  }

  /** Restart the stage: notes are read again before the start. */
  restart(): void {
    this.voice.stop();
    this.hud?.clear();
    this.preStartDone = false;
  }

  setPaused(p: boolean): void {
    if (p === this.paused) return;
    this.paused = p;
    if (p) this.voice.stop();
  }
}
